import React, { lazy, Suspense } from 'react'
import { LeftOutlined } from '@ant-design/icons'
import { UserAvatar } from '@forwardflow/ui/Avatar'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(duration)
dayjs.extend(relativeTime)

import { Avatar, Skeleton, Tooltip } from 'antd'
import Icon from '@forwardflow/ui/Icons'
import { LeadDetailData } from '@forwardflow/services/LeadService'
import {
   formatRelativeTime,
   getAvatarColor,
   getPlatformType,
} from '@forwardflow/helpers/utils'
import { formatNumber } from '@forwardflow/helpers/utils/formatNumber'
const LeadSummarySection = lazy(() => import('./LeadSummarySection'))
const LeadDetails: React.FC<LeadDetailData | any> = ({ leadDetails }) => {
   // const formatTimeAgo = (isoString: string) => {
   //    const diff = dayjs().diff(dayjs(isoString))
   //    const duration = dayjs.duration(diff)

   //    return (
   //       <span className="text-sm text-gray-400">
   //          <span className="sm:hidden">
   //             {duration.asMinutes() < 60
   //                ? `${Math.floor(duration.asMinutes())}m`
   //                : duration.asHours() < 24
   //                ? `${Math.floor(duration.asHours())}h`
   //                : `${Math.floor(duration.asDays())}d`}
   //          </span>
   //          <span className="hidden sm:inline">
   //             {dayjs(isoString).fromNow()}
   //          </span>
   //       </span>
   //    )
   // }

   return (
      <div
         className="bg-primary/10 p-6 lg:w-[300px] 2xl:w-[360px] overflow-y-auto shrink-0"
         data-testid="lead-details-container"
      >
         <div className="pb-3 mb-6 border-b border-white/10">
            <div className="flex items-center justify-between">
               <div className="flex items-center">
                  <UserAvatar
                     className="!text-[14px] font-medium mr-3 !w-10 !h-10"
                     firstName={leadDetails?.userInfo.firstName}
                     backgroundColor={
                        getAvatarColor(leadDetails?.stageInfo?.name) ||
                        undefined
                     }
                  />
                  <div>
                     <div className="truncate">{`${leadDetails?.userInfo.firstName} ${leadDetails?.userInfo.lastName}`}</div>
                     {leadDetails?.links?.fub && (
                        <a
                           href={leadDetails?.links?.fub}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="text-primary hover:underline text-sm hover:text-primary-dark"
                        >
                           See in Follow Up Boss
                        </a>
                     )}
                  </div>
               </div>
               <button className="hidden p-2 bg-gray-700 rounded-full shrink-0">
                  <LeftOutlined />
               </button>
            </div>
         </div>

         <div className="pb-5 mb-5 space-y-3 text-sm border-b border-white/10 text-white/80">
            <div className="flex items-center">
               <Icon name="Call" className="mr-3 fill-[#7E7E7E] scale-105" />
               <span
                  className={`${
                     leadDetails?.userInfo.phone
                        ? 'text-white'
                        : 'text-[#7E7E7E]'
                  }`}
               >
                  {leadDetails?.userInfo.phone}
               </span>
            </div>
            <div className="flex items-center">
               <Icon name="Email" className="mr-3 fill-[#7E7E7E] scale-105" />
               <span
                  className={`${
                     leadDetails?.userInfo.email
                        ? 'text-white'
                        : 'text-[#7E7E7E]'
                  }`}
               >
                  {leadDetails?.userInfo.email || 'No email found'}
               </span>
            </div>
            <div className="flex items-center">
               <Icon name="Home" className="mr-3 fill-[#7E7E7E]" />
               <span
                  className={`${
                     leadDetails?.propertyInfo ? 'text-white' : 'text-[#7E7E7E]'
                  }`}
               >
                  {leadDetails?.propertyInfo || 'No address found'}
               </span>
            </div>
         </div>

         {leadDetails?.leadSummary && (
            <Suspense fallback={<Skeleton active paragraph={{ rows: 2 }} />}>
               <LeadSummarySection summary={leadDetails.leadSummary} />
            </Suspense>
         )}

         {leadDetails?.questions && (
            <div className="pb-5 mb-5 space-y-4 border-b border-white/10">
               <h3 className="mb-2 text-[14px] font-medium">
                  Questions from Lead
               </h3>
               <p className="text-white/70 text-[12px]">
                  {leadDetails?.questions || 'No questions provided.'}
               </p>
            </div>
         )}

         <div className="pb-5 mb-5 text-sm border-b border-white/10">
            <h3 className="mb-3 text-[14px]">Appointment Status</h3>
            <div className="flex items-center justify-between px-3 py-2 pr-0 rounded-lg bg-black/20">
               <div className="flex flex-col text-[12px] w-full">
                  <div>
                     {!leadDetails?.appointmentInfo?.status ? (
                        <>
                           <div>No yet scheduled</div>
                        </>
                     ) : (
                        <div className="flex items-center">
                           <div className="pr-2 capitalize border-r border-white/20">
                              {leadDetails?.appointmentInfo?.status?.split(
                                 '(PLACEHOLDER)'
                              )}
                           </div>
                           <div className="px-2 text-center capitalize">
                              {`${leadDetails?.appointmentInfo?.time || ''} ${
                                 dayjs(
                                    leadDetails?.appointmentInfo?.date
                                 ).format('h:mm A, D MMM YYYY') || ''
                              }`}
                           </div>
                           <div>
                              {leadDetails?.appointmentInfo?.platform && (
                                 <div className="text-[18px] font-medium text-center border-l border-white/20 pl-2">
                                    {getPlatformType(
                                       leadDetails?.appointmentInfo?.platform?.split(
                                          '(PLACEHOLDER)'
                                       )
                                    )}
                                 </div>
                              )}
                           </div>
                        </div>
                     )}
                  </div>
               </div>
            </div>
         </div>

         <div className="pb-5 mb-5 space-y-4 text-sm border-b border-white/10">
            <div className="flex items-center gap-4">
               <span>Agent</span>

               <div className="pr-5 rounded-full flexc bg-white/10">
                  <p className="text-[12px] font-medium pr-2">
                     <Avatar
                        src={leadDetails?.agentInfo.avatar}
                        className="w-9 h-9 bg-[#851E58]"
                     >
                        {leadDetails?.agentInfo?.name?.slice(0, 1) || 'A'}
                     </Avatar>
                  </p>
                  <p className="text-[12px] font-medium">
                     {`${leadDetails?.agentInfo?.name}`}
                  </p>
               </div>
            </div>
            <div className="flex items-center gap-4">
               <span>Stage</span>
               <div className="flex items-center">
                  <Tooltip title={leadDetails?.stageInfo?.name}>
                     <span className="px-3 py-1 mr-4 truncate bg-purple-600 rounded-full">
                        {leadDetails?.stageInfo?.name || 'None'}
                     </span>
                  </Tooltip>
                  <span className="text-gray-400">
                     {formatRelativeTime(leadDetails?.stageInfo?.updatedAt)}
                  </span>
               </div>
            </div>
            <div className="flex items-center gap-4">
               <span>Source</span>
               <div className="flex items-center">
                  <span className="px-3 py-1 mr-4 rounded-full bg-white/10">
                     {leadDetails?.sourceInfo?.name || 'None'}
                  </span>
                  <span className="text-gray-400">
                     {formatRelativeTime(leadDetails?.lastUpdated)}
                  </span>
               </div>
            </div>
            <div className="flex items-center gap-4">
               <span>Lead Type</span>
               <span className="px-3 py-1 mr-4 rounded-full bg-white/10">
                  {leadDetails?.leadType || 'None'}
               </span>
            </div>
            <div className="flex items-center gap-4">
               <span className="mr-3">Tags</span>
               <div className="flex items-center gap-2 pb-1 overflow-x-auto whitespace-nowrap">
                  {leadDetails?.tags?.length > 0 ? (
                     leadDetails.tags.map((detail: string, index: number) => (
                        <span
                           key={index}
                           className="px-3 py-1 rounded-full bg-white/10 shrink-0"
                        >
                           {detail}
                        </span>
                     ))
                  ) : (
                     <span className="text-right text-gray-400">
                        No Tags Found
                     </span>
                  )}
               </div>
            </div>
         </div>

         <div className="flex flex-col gap-4 pb-6 mb-6 text-sm">
            <div className="flex items-center gap-x-7">
               <div className="flex items-center justify-between flex-1">
                  <div>Msg sent </div>
                  <div>
                     {formatNumber(leadDetails?.conversationInfo.messagesSent)}
                  </div>
               </div>
               <div className="flex items-center justify-between flex-1">
                  <div> Msg replied </div>
                  <div>
                     {formatNumber(
                        leadDetails?.conversationInfo.messagesReplied
                     )}
                  </div>
               </div>
            </div>

            <div className="flex items-center gap-x-6">
               <div className="flex items-center justify-between flex-1">
                  <div>Calls attempted</div>
                  <div>
                     {formatNumber(
                        leadDetails?.conversationInfo.callsAttempted
                     )}
                  </div>
               </div>
               <div className="flex items-center justify-between flex-1">
                  <div>Calls answered </div>
                  <div>
                     {' '}
                     {formatNumber(leadDetails?.conversationInfo.callsAnswered)}
                  </div>
               </div>
            </div>
         </div>
      </div>
   )
}
export default LeadDetails
